import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { frontendDb as db } from '../lib/frontend-db';
import BieberHeader from './BieberHeader';
import BieberFooter from './BieberFooter';
import DynamicFontLoader from './DynamicFontLoader';

interface BieberInfoData {
  title: string;
  subtitle: string;
  hero_image: string;
  content: string;
  contact_info: string;
  gallery_images: string[];
  history_text: string;
  activities_text: string;
  back_to_stimmart_text: string;
}

const BieberInfo = () => {
  const [footerSettings, setFooterSettings] = useState({
    backgroundColor: '#2d5a2f',
    textColor: '#ffffff',
    linkColor: '#ffffff',
    linkHoverColor: 'rgba(255, 255, 255, 0.8)'
  });
  const [data, setData] = useState<BieberInfoData>({
    title: 'Liederkranz Bieber',
    subtitle: 'Tradition und Musik seit 1847',
    hero_image: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=1200&h=600&fit=crop',
    content: 'Der Liederkranz Bieber 1847 e.V. blickt auf eine lange Tradition zurück. Seit über 175 Jahren pflegen wir das deutsche Liedgut und bringen Menschen durch Musik zusammen.',
    contact_info: 'Kontakt:\nLiederkranz Bieber 1847 e.V.\nWürzburger Straße 1\n63599 Biebergemünd\nTelefon: 06050 / 1234567\nE-Mail: info@liederkranz-bieber.de',
    gallery_images: [
      'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=400&h=300&fit=crop',
      'https://images.unsplash.com/photo-1493225457124-a3eb161ffa5f?w=400&h=300&fit=crop',
      'https://images.unsplash.com/photo-1516450360452-9312f5e86fc7?w=400&h=300&fit=crop'
    ],
    history_text: 'Unsere Geschichte reicht bis ins Jahr 1847 zurück und ist geprägt von musikalischen Höhepunkten und gesellschaftlichen Ereignissen. Über die Jahrzehnte haben wir uns zu einem wichtigen kulturellen Bestandteil der Gemeinde entwickelt.',
    activities_text: 'Wir bieten regelmäßige Proben, Konzerte und gesellschaftliche Veranstaltungen. Neue Mitglieder sind herzlich willkommen und können jederzeit zu unseren Proben kommen.',
    back_to_stimmart_text: 'Entdecken Sie auch unseren Hauptverein StimmArt und erfahren Sie mehr über unsere gemeinsamen musikalischen Aktivitäten und Veranstaltungen.'
  });
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const settings = await db.getSettings();
      if (settings) {
        // Load footer settings
        const bieberFooterSettings = {
          backgroundColor: (settings as any).bieber_footer_bg_color || footerSettings.backgroundColor,
          textColor: (settings as any).bieber_footer_text_color || footerSettings.textColor,
          linkColor: (settings as any).bieber_footer_link_color || footerSettings.linkColor,
          linkHoverColor: (settings as any).bieber_footer_link_hover_color || footerSettings.linkHoverColor,
        };
        setFooterSettings(bieberFooterSettings);
        
        // Apply colors to CSS variables
        document.documentElement.style.setProperty('--bieber-primary-color', bieberFooterSettings.backgroundColor);
        document.documentElement.style.setProperty('--bieber-text-color', bieberFooterSettings.textColor);
        document.documentElement.style.setProperty('--bieber-link-color', bieberFooterSettings.linkColor);
        
        // Apply font family if set
        if (settings.font_family && settings.font_family !== 'System Default') {
          const fontFamilyWithFallbacks = `"${settings.font_family}", sans-serif`;
          document.documentElement.style.setProperty('--font-family', fontFamilyWithFallbacks);
          document.body.style.fontFamily = fontFamilyWithFallbacks;
          console.log('Applied font family in BieberInfo:', fontFamilyWithFallbacks);
        }
        
        // Load content data
        const bieberData = {
          title: (settings as any).bieber_info_title || data.title,
          subtitle: (settings as any).bieber_info_subtitle || data.subtitle,
          hero_image: (settings as any).bieber_info_hero_image || data.hero_image,
          content: (settings as any).bieber_info_content || data.content,
          contact_info: (settings as any).bieber_info_contact || data.contact_info,
          gallery_images: (settings as any).bieber_info_gallery ? 
            JSON.parse((settings as any).bieber_info_gallery) : data.gallery_images,
          history_text: (settings as any).bieber_info_history || data.history_text,
          activities_text: (settings as any).bieber_info_activities || data.activities_text,
          back_to_stimmart_text: (settings as any).bieber_back_to_stimmart_text || data.back_to_stimmart_text,
        };
        setData(bieberData);
      }
    } catch (error) {
      console.error('Error loading Bieber info:', error);
      setError('Fehler beim Laden der Informationen. Bitte versuchen Sie es später erneut.');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen transition-colors duration-300" style={{ backgroundColor: 'var(--bg-primary)' }}>
        <BieberHeader />
        <div className="mt-16">
          <div className="h-96 animate-pulse" style={{ backgroundColor: 'var(--bg-secondary)' }} />
          <div className="container mx-auto px-4 py-16">
            <div className="space-y-8">
              {[1, 2, 3].map((i) => (
                <div key={i} className="space-y-4">
                  <div className="h-8 rounded w-1/3 animate-pulse" style={{ backgroundColor: 'var(--bg-secondary)' }} />
                  <div className="space-y-2">
                    <div className="h-4 rounded animate-pulse" style={{ backgroundColor: 'var(--bg-secondary)' }} />
                    <div className="h-4 rounded w-5/6 animate-pulse" style={{ backgroundColor: 'var(--bg-secondary)' }} />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
        <BieberFooter />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen transition-colors duration-300" style={{ backgroundColor: 'var(--bg-primary)' }}>
        <BieberHeader />
        <div className="container mx-auto px-4 py-16 mt-16">
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-6 text-center">
            <h2 className="text-xl font-semibold text-red-800 mb-2">Fehler</h2>
            <p className="text-red-600 dark:text-red-400">{error}</p>
          </div>
        </div>
        <BieberFooter />
      </div>
    );
  }

  return (
    <div className="min-h-screen transition-colors duration-300" style={{ backgroundColor: 'var(--bg-primary)', '--primary-color': footerSettings.backgroundColor } as React.CSSProperties}>
      <BieberHeader />
      
      {/* Hero Section */}
      <div id="hero" className="relative h-96 mt-16">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${data.hero_image})` }}
        >
          <div className="absolute inset-0 bg-black/50" />
        </div>
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="text-center text-white">
            <h1 className="text-4xl md:text-6xl font-bold mb-4">{data.title}</h1>
            <p className="text-xl md:text-2xl">{data.subtitle}</p>
          </div>
        </div>
      </div>

      <main className="container mx-auto px-4 py-16">
        {/* Hauptinhalt */}
        <section className="mb-16">
          <div className="max-w-4xl mx-auto">
            <div className="prose prose-lg mx-auto text-center">
              {data.content.split('\n').map((paragraph, index) => (
                <p key={index} className="mb-4 text-lg leading-relaxed" style={{ color: 'var(--text-primary)' }}>
                  {paragraph}
                </p>
              ))}
            </div>
          </div>
        </section>

        {/* Geschichte */}
        <section id="geschichte" className="mb-16">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold mb-8 text-center" style={{ color: footerSettings.backgroundColor }}>
              Unsere Geschichte
            </h2>
            <div className="prose prose-lg mx-auto text-center">
              {data.history_text.split('\n').map((paragraph, index) => (
                <p key={index} className="mb-4 text-lg leading-relaxed" style={{ color: 'var(--text-primary)' }}>
                  {paragraph}
                </p>
              ))}
            </div>
          </div>
        </section>

        {/* Galerie */}
        <section id="galerie" className="mb-16">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl font-bold mb-8 text-center" style={{ color: footerSettings.backgroundColor }}>
              Bilder
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {data.gallery_images.map((image, index) => (
                <div 
                  key={index} 
                  className="aspect-w-16 cursor-pointer"
                  onClick={() => setSelectedImage(image)}
                >
                  <img 
                    src={image} 
                    alt={`Galerie Bild ${index + 1}`}
                    className="w-full h-64 object-cover rounded-lg shadow-md hover:opacity-90 transition-opacity"
                  />
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Aktivitäten */}
        <section id="aktivitaeten" className="mb-16">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold mb-8 text-center" style={{ color: footerSettings.backgroundColor }}>
              Unsere Aktivitäten
            </h2>
            <div className="prose prose-lg mx-auto text-center">
              {data.activities_text.split('\n').map((paragraph, index) => (
                <p key={index} className="mb-4 text-lg leading-relaxed" style={{ color: 'var(--text-primary)' }}>
                  {paragraph}
                </p>
              ))}
            </div>
          </div>
        </section>

        {/* Kontakt */}
        <section id="kontakt" className="rounded-lg p-8" style={{ backgroundColor: 'var(--bg-secondary)' }}>
          <div className="max-w-2xl mx-auto">
            <h2 className="text-3xl font-bold mb-8 text-center" style={{ color: footerSettings.backgroundColor }}>
              Kontakt
            </h2>
            <div className="rounded-lg shadow-md p-6" style={{ backgroundColor: 'var(--bg-primary)' }}>
              <div className="whitespace-pre-line text-lg leading-relaxed text-center" style={{ color: 'var(--text-primary)' }}>
                {data.contact_info}
              </div>
            </div>
          </div>
        </section>

        {/* Zurück zu StimmArt Section */}
        <section className="mb-16 rounded-lg p-8" style={{ backgroundColor: footerSettings.backgroundColor }}>
          <div className="max-w-2xl mx-auto text-center">
            <h2 className="text-3xl font-bold mb-6" style={{ color: footerSettings.textColor }}>
              Zurück zu StimmArt
            </h2>
            <p className="text-lg mb-6 leading-relaxed" style={{ color: footerSettings.textColor }}>
              {data.back_to_stimmart_text}
            </p>
            <Link
              to="/"
              className="inline-block px-8 py-3 rounded-md font-semibold transition-all duration-200 hover:opacity-90 transform hover:scale-105"
              style={{ 
                backgroundColor: footerSettings.textColor, 
                color: footerSettings.backgroundColor,
                border: `2px solid ${footerSettings.textColor}`
              }}
            >
              Zu StimmArt wechseln
            </Link>
          </div>
        </section>
      </main>

      {/* Image Modal */}
      {selectedImage && (
        <div 
          className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4"
          onClick={() => setSelectedImage(null)}
        >
          <div className="max-w-4xl w-full">
            <img 
              src={selectedImage} 
              alt="Galerie Bild"
              className="w-full h-auto max-h-[90vh] object-contain rounded-lg"
            />
          </div>
        </div>
      )}

      <BieberFooter />
      <DynamicFontLoader />
    </div>
  );
};

export default BieberInfo; 

